import React, { useState } from 'react';
import {
  TextField,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
} from '@mui/material';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';
import Navbar from '../../../Navbar';
import { supabase } from '../../../supabaseClient';

import YouTubeIcon from '@mui/icons-material/YouTube';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ActivitiesForm = ({ selectedSession, activities, setActivities }) => {
  const [type, setType] = useState('');
  const [title, setTitle] = useState('');
  const [score, setScore] = useState('100');
  const [videoUrl, setVideoUrl] = useState('');
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [alternatives, setAlternatives] = useState(['', '', '', '']);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [requestData, setRequestData] = useState(false);

  const handleTypeChange = (e) => {
    setType(e.target.value);
  };

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };

  const handleScoreChange = (e) => {
    setScore(e.target.value);
  };

  const handleVideoUrlChange = (e) => {
    setVideoUrl(e.target.value);
  };

  const handleQuestionChange = (e) => {
    setNewQuestion(e.target.value);
  };

  const handleAlternativeChange = (e, index) => {
    const updatedAlternatives = [...alternatives];
    updatedAlternatives[index] = e.target.value;
    setAlternatives(updatedAlternatives);
  };

  const deleteActivity = async (activityId) => {
    try {
      const { data, error } = await supabase.from('activities').delete().eq('id', activityId);

      if (error) {
        throw new Error(error.message);
      }

      console.log('Activity deleted successfully');
    } catch (error) {
      console.error('Error deleting activity:', error);
    }
  };

  const addActivity = async (activityData) => {
    try {
      const { data, error } = await supabase.from('activities').insert(activityData);

      if (error) {
        throw new Error(error.message);
      }

      console.log('Activity added successfully');
    } catch (error) {
      console.error('Error adding activity:', error);
    }
  };

  const handleDeleteActivity = (index, id) => {
    const updatedActivities = [...activities];
    updatedActivities.splice(index, 1);
    setActivities(updatedActivities);

    deleteActivity(id);
  };

  const handleActivitySubmit = (e) => {
    e.preventDefault();
    try {
      let newActivity = {
        type: type,
        score: score,
        session: selectedSession.id,
      };

      if (type === 'video') {
        newActivity.title = title;
        newActivity.metadata = JSON.stringify({
          videoUrl: videoUrl,
        });
      } else if (type === 'quiz') {
        newActivity.metadata = JSON.stringify({
          quiz: quizQuestions,
        });
      } else if (type === 'attendance') {
        newActivity.metadata = JSON.stringify({
          requestData: requestData,
        });
      }

      addActivity(newActivity);
      setActivities([...activities, newActivity]);

      setType('');
      setTitle('');
      setScore('100');
      setVideoUrl('');
      setQuizQuestions([]);
      setNewQuestion('');
      setAlternatives(['', '', '', '']);
      setCorrectAnswer('');
      setRequestData(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddQuestion = (e) => {
    e.preventDefault();

    const question = {
      question: newQuestion,
      alternatives: alternatives,
      correctAnswer: correctAnswer,
    };

    setQuizQuestions([...quizQuestions, question]);
    setNewQuestion('');
    setAlternatives(['', '', '', '']);
    setCorrectAnswer('');
  };

  const handleRemoveQuestion = (index) => {
    const updatedQuestions = [...quizQuestions];
    updatedQuestions.splice(index, 1);
    setQuizQuestions(updatedQuestions);
  };

  const handleRemoveAlternative = (index) => {
    const updatedAlternatives = [...alternatives];
    updatedAlternatives.splice(index, 1);
    setAlternatives(updatedAlternatives);
  };

  const handleAddAlternative = () => {
    setAlternatives([...alternatives, '']);
  };

  const handleAlternativeSelection = (index) => {
    setCorrectAnswer(index);
  };

  const handleRequestDataChange = (e) => {
    setRequestData(e.target.checked);
  };

  const renderVideoForm = () => {
    return (
      <>
        <Grid item md={12}>
          <TextField label="Nombre" value={title} onChange={handleTitleChange} required fullWidth />
        </Grid>
        <Grid item md={12}>
          <TextField
            label="URL del video en YouTube"
            value={videoUrl}
            onChange={handleVideoUrlChange}
            required
            fullWidth
          />
        </Grid>
      </>
    );
  };

  const renderQuizForm = () => {
    return (
      <>
        <Grid item md={12}>
          <TextField label="Pregunta" value={newQuestion} onChange={handleQuestionChange} fullWidth />
        </Grid>
        {alternatives.map((alternative, index) => (
          <Grid item md={12} key={index}>
            <TextField
              label={`Alternativa ${index + 1} ${index === 0 ? '(Correcta)' : ''}`}
              value={alternative}
              onChange={(e) => handleAlternativeChange(e, index)}
              fullWidth
            />
          </Grid>
        ))}
        <Grid item md={12}>
          <Button type="submit" variant="contained" color="primary" onClick={handleAddQuestion}>
            Agregar Pregunta
          </Button>
        </Grid>
        <Grid item md={12}>
          {quizQuestions.map((question, index) => (
            <div key={index}>
              <Typography variant="h6">{question.question}</Typography>
              <ul>
                {question.alternatives.map((alternative, i) => (
                  <li key={i}>{correctAnswer === i ? <strong>{alternative}</strong> : alternative}</li>
                ))}
              </ul>
              <Button variant="contained" color="error" onClick={() => handleRemoveQuestion(index)}>
                Eliminar Pregunta
              </Button>
            </div>
          ))}
        </Grid>
      </>
    );
  };

  const renderAttendanceForm = () => {
    return (
      <>
        <Grid item md={12}>
          <FormControl>
            <Checkbox checked={requestData} onChange={handleRequestDataChange} />
            <Typography variant="body1">Pedir datos</Typography>
          </FormControl>
        </Grid>
      </>
    );
  };

  return (
    <>
      <ActivityList activities={activities} handleDeleteActivity={handleDeleteActivity} />

      <form onSubmit={handleActivitySubmit}>
        <Grid container spacing={2}>
          <Grid item md={12}>
            <FormControl required fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select value={type} onChange={handleTypeChange}>
                <MenuItem value="video">Video</MenuItem>
                <MenuItem value="quiz">Quiz</MenuItem>
                <MenuItem value="attendance">Asistencia</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item md={12}>
            <TextField label="Puntaje" type="number" value={score} onChange={handleScoreChange} required fullWidth />
          </Grid>

          {type === 'video' && renderVideoForm()}
          {type === 'quiz' && renderQuizForm()}
          {type === 'attendance' && renderAttendanceForm()}
          <Grid item md={12}>
            <Button type="submit" variant="contained" color="primary" fullWidth>
              Agregar Actividad
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

const ActivityList = ({ activities, handleDeleteActivity }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case 'video':
        return <YouTubeIcon />;
      case 'quiz':
        return <AssignmentIcon />;
      case 'attendance':
        return <CheckCircleIcon />;
      default:
        return <InsertDriveFile />;
    }
  };

  return (
    <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
      {activities &&
        activities.map((activity, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              <ListItemIcon>{getActivityIcon(activity.type)}</ListItemIcon>
            </ListItemIcon>
            <ListItemText primary={activity.type} secondary={activity.title} />
            <ListItemText primary={activity.score} />
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDeleteActivity(index, activity.id)}
            >
              Borrar
            </Button>
          </ListItem>
        ))}
    </List>
  );
};

export default ActivitiesForm;
